// Shared shape of a POST /audiobooks/create request.
//
// Both storybook entry points use this: the manual wizard at /audiobooks/create
// and the one-shot topic flow at /storybook-template. Keeping the payload in
// one place is what stops the two from drifting into queueing different books.

export const STORY_LENGTH_OPTIONS = [
  { value: 8, label: "8 pages", stars: 50 },
  { value: 25, label: "25 pages", stars: 75 },
  { value: 50, label: "50 pages", stars: 100 },
];

export const READING_LEVEL_OPTIONS = [
  { value: 1, label: "Level 1", sub: "Ages 2–4" },
  { value: 2, label: "Level 2", sub: "Ages 4–5" },
  { value: 3, label: "Level 3", sub: "Ages 5–6" },
  { value: 4, label: "Level 4", sub: "Ages 6–7" },
  { value: 5, label: "Level 5", sub: "Ages 7–8" },
];

export const DEFAULT_PAGE_COUNT = 8;
export const DEFAULT_READING_LEVEL = 1;
export const MAX_STORYBOOK_CHARACTERS = 3;

export function getStoryLengthStarCost(pageCount) {
  const matched = STORY_LENGTH_OPTIONS.find((option) => option.value === pageCount);
  return matched?.stars ?? STORY_LENGTH_OPTIONS[0].stars;
}

/**
 * Build the multipart body for /audiobooks/create.
 *
 * Only the fields the endpoint actually binds are sent. In particular the lead
 * character's artwork is NOT uploaded: `create_audiobook` reads
 * `animated_image_url` off the character row it just validated, so uploading a
 * blob only cost a round trip nobody read. The parent voice comes from
 * `users.parent_cloned_voice_id` for the same reason — `voice_source` alone
 * selects it. The active profile travels as the `X-Profile-Id` header that
 * APIClient puts on every authenticated request, so it is not a body field.
 */
export function buildAudiobookCreateFormData({
  userId,
  characters = [],
  title = "",
  description = "",
  isPublic = false,
  pageCount = DEFAULT_PAGE_COUNT,
  readingLevel = DEFAULT_READING_LEVEL,
  voiceSource = "random",
}) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("character_ids", characters.map((c) => c.id).join(","));
  formData.append("title", (title || "").trim());
  formData.append("description", (description || "").trim());
  formData.append("is_public", isPublic);
  formData.append("page_count", String(pageCount));
  formData.append("reading_level", readingLevel || DEFAULT_READING_LEVEL);
  formData.append("voice_source", voiceSource);
  return formData;
}

/** The backend has spelled this three ways over time; accept all of them. */
export function extractAudiobookId(response) {
  return response?.data?.audiobook_id || response?.audiobook_id || response?.id || null;
}

export function extractRemainingStars(response) {
  return response?.remaining_stars ?? response?.data?.remaining_stars;
}
